import axios from 'axios'
import router from '../router'
import { Message } from 'element-ui'
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '../utils/contant/contant'
import { encryptBase64, encryptWithAes, generateAesKey, decryptWithAes, decryptBase64 } from '@/utils/crypto'
import { encrypt, decrypt } from '@/utils/jsencrypt'

const encryptHeader = 'encrypt-key'

axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8'

const http = axios.create({
	baseURL: process.env.VUE_APP_BASE_API,
	timeout: 1000 * 30
})

// 是否正在刷新 token
let isRefreshing = false

// 401 等待队列
let requestQueue = []

// 添加
const addQueue = callback => {
	requestQueue.push(callback)
}

const runQueue = token => {
	requestQueue.forEach(callback => callback(token))
	requestQueue = []
}

/** 并发 401/400 等会多次调用 exit；emit 一次即可，否则会触发多个 exit 监听各自请求 /logout */
let exitEmitted = false

const exit = () => {
	if (exitEmitted) {
		return
	}
	exitEmitted = true
	sessionStorage.removeItem(ACCESS_TOKEN_KEY)
	sessionStorage.removeItem(REFRESH_TOKEN_KEY)
	router.app.$eventBus.$emit('exit')
}

/**
 * 请求拦截
 */
http.interceptors.request.use(config => {
	const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY)

	if (accessToken && exitEmitted) {
		exitEmitted = false
	}

	if (accessToken) {
		config.headers.accessToken = encodeURIComponent(accessToken)
	}

	if (process.env.VUE_APP_ENCRYPT === 'true') {
		const isUploadRequest = config.data instanceof FormData
		const isWebrtcRequest = config.url && config.url.includes('/webrtc')

		if (
			!isUploadRequest &&
			!isWebrtcRequest &&
			['post', 'put'].includes(config.method && config.method.toLowerCase())
		) {
			const aesKey = generateAesKey()
			config.headers[encryptHeader] = encrypt(encryptBase64(aesKey))
			config.data = typeof config.data === 'object'
				? encryptWithAes(JSON.stringify(config.data), aesKey)
				: encryptWithAes(config.data, aesKey)
		}
	}

	return config
}, error => {
	return Promise.reject(error)
})

/**
 * 响应拦截
 */
http.interceptors.response.use(async response => {
	if (process.env.VUE_APP_ENCRYPT === 'true') {
		const keyStr = response.headers[encryptHeader]

		if (keyStr != null && keyStr !== '') {
			const data = response.data
			const base64Str = decrypt(keyStr)
			const aesKey = decryptBase64(base64Str.toString())
			const decryptData = decryptWithAes(data, aesKey)
			response.data = JSON.parse(decryptData)
		}
	}

	const res = response.data

	if (res.code === 200) {
		return res.data || res
	}

	if (res.code === 400) {
		exit()
		return Promise.reject(res)
	}

	if (res.code === 401) {
		const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY)
		const originalConfig = response.config

		if (!refreshToken) {
			exit()
			return Promise.reject(res)
		}

		// 防止 refreshToken 接口自身 401 后死循环
		if (originalConfig.url === '/refreshToken') {
			exit()
			return Promise.reject(res)
		}

		// 已有请求正在刷新 token，当前请求排队等待
		if (isRefreshing) {
			return new Promise((resolve, reject) => {
				addQueue(token => {
					if (!token) {
						reject(res)
						return
					}

					originalConfig.headers.accessToken = encodeURIComponent(token)
					resolve(http(originalConfig))
				})
			})
		}

		isRefreshing = true

		try {
			const data = await http({
				method: 'put',
				url: '/refreshToken',
				headers: {
					refreshToken
				}
			})

			sessionStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken)
			sessionStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken)

			runQueue(data.accessToken)

			originalConfig.headers.accessToken = encodeURIComponent(data.accessToken)
			return http(originalConfig)
		} catch (e) {
			runQueue(null)
			exit()
			return Promise.reject(e)
		} finally {
			isRefreshing = false
		}
	}

	Message({
		message: res.message,
		type: 'error',
		duration: 1500,
		customClass: 'element-error-message-zindex'
	})

	return Promise.reject(res)
}, error => {
	if (!error.response) {
		Message({
			message: '网络异常，请检查网络连接',
			type: 'error',
			duration: 1500,
			customClass: 'element-error-message-zindex'
		})

		return Promise.reject(error)
	}

	switch (error.response.status) {
		case 400:
			Message({
				message: error.response.data,
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break

		case 401:
			exit()
			break

		case 405:
			Message({
				message: 'http请求方式有误',
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break

		case 404:
		case 500:
			Message({
				message: '服务器出了点小差，请稍后再试',
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break

		case 501:
			Message({
				message: '服务器不支持当前请求所需要的某个功能',
				type: 'error',
				duration: 1500,
				customClass: 'element-error-message-zindex'
			})
			break
	}

	return Promise.reject(error)
})

export default http